import { z } from "zod";
import { readStoredClinicalPhase } from "./clinical-phase";
import type { PatientGroup } from "./patient-group";
import type { PrescriptionTransferReviewStatus } from "./prescription-transfer-review";

// TES-398 — "Dina steg" as its own progression surface (spec,
// 2026-08-12). Reads the SAME journey signals CP ("Vad händer just nu") and
// DT ("För dig idag") read — this derivation adds no new state, only a
// presentation: what's done, what's active, what completing it unlocks.
//
// Design decisions from the spec:
//   - Each step has a TYPE: `task` (patient does something), `milestone`
//     (reached/achieved), `feedback` (the care team responded), `next_step`
//     (guidance about what comes next — the patient WAITS, it is not their
//     job). No personal insights in MVP.
//   - Booking + payment are ONE step (the app's ConsultPayment → Booking
//     flow is already built that way).
//   - "Blodprov, om det behövs" — always listed since TES-407; active only
//     once the doctor has ordered bloods, "ej aktuellt" after the meeting
//     otherwise (TES-1127).
//   - Copy ("När detta är klart" etc.) lives client-side keyed by step key
//     for MVP; the derivation ships keys + types + statuses only, so the
//     copy can move to server content later without touching this shape.
//
// Three catalogs by macro-state:
//   pre_treatment — the linear onboarding ladder (f01 → … → subscription).
//                   TES-450: a prescription transfer has its OWN six-step
//                   ladder (09 Surface Rules) — she never books a
//                   consultation, so the consultation rungs must never render
//                   for her.
//   active        — released: the start steps until her first dose, then
//                   nothing (TES-1634: "Nästa receptförnyelse" replaces
//                   Dina steg under active treatment).
//   at_goal       — goal reached / maintenance.

export const JOURNEY_STEP_TYPES = [
  "task",
  "milestone",
  "feedback",
  "next_step",
] as const;
export const journeyStepTypeSchema = z.enum(JOURNEY_STEP_TYPES);
export type JourneyStepType = z.infer<typeof journeyStepTypeSchema>;

export const journeyStepStatusSchema = z.enum([
  "done",
  "active",
  "upcoming",
  "attention",
  // TES-407 — the doctor decided the step isn't needed ("ej aktuellt").
  // Rendered explicitly, never silently missing ("Blodprov vid
  // behov" must always be visible in the journey).
  "skipped",
]);
export type JourneyStepStatus = z.infer<typeof journeyStepStatusSchema>;

export const JOURNEY_STEP_KEYS = [
  // Before treatment start
  "f01",
  // TES-587 — Resultat B + "Avsluta för nu": the ladder pauses here.
  "f01_paused",
  "intake",
  "book_consult",
  "consult",
  "labs",
  "treatment_decision",
  "subscription",
  // TES-1150 (Emelie 2026-10-07) — "Hämta ut läkemedlet, när uthämtning
  // krävs": between the activated subscription and the start, only once the
  // verified subscription has released a NEW prescription and until she has
  // collected it. TES-1159: on the released patient's start steps, for both
  // patient groups. App builds before 2.0.7 have no
  // bundled copy for the key; the server always sends it (waiting-step-copy).
  "medication_pickup",
  "treatment_start",
  // TES-740 — CP-06 after a positive decision with follow-up remaining
  // (TES-736): "Medicinsk uppföljning", marked "Kan göras parallellt". It
  // replaces the labs rung the moment the doctor has decided, and rides
  // beside the active ladder once the treatment is live — never a rung she
  // has to pass before "Behandlingsstart".
  "medical_follow_up",
  // Prescription transfer (TES-450, 09 Surface Rules). f01 is shared; the
  // rest is the transfer track's own ladder. No consultation rung, by
  // decision.
  "transfer_declaration",
  "transfer_review",
  "transfer_completion",
  "transfer_decision",
  "transfer_continued",
  // Under behandling
  "first_week",
  "weekly_checkin",
  "care_feedback",
  "monthly_followup",
  "renewal",
  "plan_updated",
  // At goal weight
  "goal_reached",
  "maintenance_plan",
  "stabilisation",
  "continued_followup",
  "adjust_goal",
] as const;
export const journeyStepKeySchema = z.enum(JOURNEY_STEP_KEYS);
export type JourneyStepKey = z.infer<typeof journeyStepKeySchema>;

// TES-473 (Ö-6) — a recurring step is NOT a rung. "Fortsatt uppföljning"
// flips done/active on the ISO week's check-in, while its
// neighbours follow phase progression, so without this mark a later step went
// green before an earlier one. `status` keeps meaning "this week"; `cadence`
// tells the surface to draw it as a loop, apart from the ladder. Optional so
// an app build that predates it simply ignores the field.
export const journeyStepCadenceSchema = z.enum(["weekly"]);
export type JourneyStepCadence = z.infer<typeof journeyStepCadenceSchema>;

export const journeyStepSchema = z.object({
  key: journeyStepKeySchema,
  type: journeyStepTypeSchema,
  status: journeyStepStatusSchema,
  cadence: journeyStepCadenceSchema.optional(),
  // TES-740 — a parallel track, not a rung (Emelie 2026-09-16:
  // "Medicinsk uppföljning … märkt Kan göras parallellt"). Like `cadence`: the
  // surface
  // draws it apart from the ladder, and the positional derivation never
  // lets it block the rung after it. Optional so an older app ignores it.
  parallel: z.boolean().optional(),
});
export type JourneyStep = z.infer<typeof journeyStepSchema>;

export const journeyStepsPhaseSchema = z.enum([
  "pre_treatment",
  "active",
  "at_goal",
]);
export type JourneyStepsPhase = z.infer<typeof journeyStepsPhaseSchema>;

export const journeyStepsSchema = z.object({
  phase: journeyStepsPhaseSchema,
  steps: z.array(journeyStepSchema),
  // TES-896 step 3 — whether she has a personal coach, so the surface can
  // pick coach-free wording for the two steps whose copy names the coach
  // (`first_week` body, `weekly_checkin` unlocks). Canon: coach copy follows
  // `coach_access`, never the clinical phase. Optional so an app build that
  // predates it ignores the field, and a server that predates it reads as
  // "active" — the same default `plannedCoachAccessForPatient` uses for a
  // null plan (TES-1121: the planned access, so onboarding keeps the coach
  // wording for the journey ahead).
  coachAccess: z.enum(["active", "inactive"]).optional(),
});
export type JourneySteps = z.infer<typeof journeyStepsSchema>;

export interface JourneyStepsInput {
  // TES-450 — which ladder applies before treatment. Derived from the F-01
  // answer (resolvePatientGroup); the consultation ladder is the default.
  patientGroup: PatientGroup;
  // TES-450 — the transfer review row (TES-457). Null until F-02 + F-07 are
  // both in — its existence IS "underlaget är inlämnat". `completionEver-
  // Requested` = the doctor asked for a komplettering at some point
  // (requestedCompletionGroups non-empty), so step 4 can read done after the
  // resubmit instead of vanishing.
  transferReview: {
    status: PrescriptionTransferReviewStatus;
    completionEverRequested: boolean;
  } | null;
  // Pre-treatment signals (same facts /me already loads).
  f01Done: boolean;
  // TES-587 — the latest F-01 was an early stop (Resultat B) and the patient
  // chose "Avsluta för nu"; no meeting since. The ladder shows
  // "Din resa är pausad" and never renders "Boka vårdmöte" as the next step.
  f01Paused?: boolean;
  // TES-587 — Resultat B shown but no choice made yet (she left the result
  // page): booking is not the next step until she chooses it herself.
  f01Stopped?: boolean;
  hasIntake: boolean;
  intakeClean: boolean;
  // TES-675 / CP-13 — an uncleared MedicalRiskFlag: she
  // answered F-02's self-harm question "ja" and no doctor has closed it.
  //
  // Required, not optional-with-a-default, on purpose. A new surface that
  // assembles these signals and forgets this one would silently show her the
  // ordinary rejection copy again — which is the exact defect this ticket
  // exists for. A compile error is the right way to find that out.
  acuteSafetyActive: boolean;
  consultBooked: boolean; // a meeting exists (scheduled/in_progress/completed)
  consultHeld: boolean; // a meeting reached status "completed"
  // TES-1601 (TES-1600 S1) — the tenant's `careMeetingOptional` is on and she
  // is a new-treatment patient: no meeting stands between the health
  // declaration and the doctor's written decision. Optional so every caller
  // that does not pass it keeps the meeting-first ladder.
  onboardingMeetingOptional?: boolean;
  labsOrdered: boolean;
  // TES-537 — the doctor's documented next step waived labs (results verified
  // in a trusted care source). Optional so older callers keep compiling.
  labsWaived?: boolean;
  treatmentActive: boolean;
  entitled: boolean;
  // TES-1150 — the released FIRST prescription is a new one she has not yet
  // collected (CP-12 variant A: `medicationPickupRequired`, no "Jag har
  // hämtat ut läkemedlet", no first dose). While true the start steps show
  // "Hämta ut läkemedlet" active (TES-1159). Optional so an older caller
  // reads as before (no pickup rung).
  medicationPickupOutstanding?: boolean;
  // Under-treatment signals.
  weeklyCheckinSubmitted: boolean; // this ISO week
  // TES-1158 — does the CURRENT check-in window count for her: it opened
  // after her treatment started (for ACTIVE_TREATMENT, her registered first
  // dose). False keeps the check-in rung "upcoming" instead of "active", so
  // Dina steg never marks a check-in as waiting from before her first dose.
  // Optional so an older caller reads as before (counts).
  weeklyCheckinCounts?: boolean;
  // Doctor lab feedback sent on the latest order. Read by the pre-treatment
  // labs rung only. TES-1159: it no longer drives the under-treatment
  // feedback step, which is the coach's reply to the weekly check-in.
  careFeedbackReceived: boolean;
  // TES-1159 (Emelie 2026-10-07, point 6) — her treatment is released and
  // she is asked to register a first dose that she has not registered yet
  // (firstDoseState().canReport: ACTIVE_TREATMENT, live cycle, entitled).
  // While true, "Behandlingsstart" is the current step and the first week is
  // not active yet. Optional so an older caller reads as before (no
  // first-dose gate).
  firstDoseOutstanding?: boolean;
  // At-goal signals.
  // TES-1023 (Ö-277 a) — the doctor has decided weight stabilisation. The
  // field keeps its old name because apps in the field read it; the server
  // fills it through `goalTrackReached`, never from the weight alone.
  goalReached: boolean;
  inMaintenance: boolean; // derived treatment phase = maintenance
  // TES-740 — the CP-06 checklist is still open after the treatment
  // decision (TES-736). Read from medicalEvidenceChecklist().active; only
  // meaningful once `treatmentActive`.
  medicalFollowUpOpen: boolean;
  // TES-1094 — the newest PUBLISHED treatment decision
  // (`publishedTreatmentDecision`: the CP-09 notice, or the decided transfer
  // review). Emelie's canonical CP-06 rule, point 7: "Behandlingsbeslut" is
  // done only once a decision has been published for a final outcome, and
  // point 8: the "doctor has approved" copy only after a published positive
  // one. An active treatment row is not that fact: a test or legacy path can
  // leave `Treatment.state` active with nothing published, and the ladder then
  // ticked the decision and promised the start.
  //
  // Required on purpose, like `acuteSafetyActive`: a surface that forgets it
  // must fail to compile rather than tick a decision nobody made.
  publishedDecision: "none" | "approved" | "declined";
  // TES-1094 — every CP-06 task of hers is done and the case is still open
  // (medicalEvidenceChecklist(): active && allDone). Point 1:
  // `patient_evidence_tasks_completed` only means her tasks in Ella are done;
  // it says nothing about the result, the evidence or a decision.
  medicalEvidenceWaiting: boolean;
  // TES-1285 — the open CP-06 case asks for a new blood test whose payment is
  // not verified yet. Optional: a surface that does not pass it keeps the
  // `labs` rung as before.
  labsAwaitingPayment?: boolean;
  // TES-896 — the patient's coach access (from her plan, never her phase).
  // Passed through to the result; it changes wording, not which steps show.
  coachAccess: "active" | "inactive";
}

const parallelStep = (
  key: JourneyStepKey,
  type: JourneyStepType,
): JourneyStep => ({
  key,
  type,
  status: "active",
  parallel: true,
});

const step = (
  key: JourneyStepKey,
  type: JourneyStepType,
  status: JourneyStepStatus,
  cadence?: JourneyStepCadence,
): JourneyStep =>
  cadence ? { key, type, status, cadence } : { key, type, status };

// The pre-treatment ladder: strictly positional — everything before the
// first not-done step is done, everything after is upcoming.
// TES-1150 — the "Hämta ut läkemedlet" rung: the treatment is released (live
// and entitled) and her new prescription is still to be collected.
function pickupRung(s: JourneyStepsInput): boolean {
  return (
    s.treatmentActive && s.entitled && s.medicationPickupOutstanding === true
  );
}

function preTreatmentSteps(s: JourneyStepsInput): JourneyStep[] {
  // TES-587 (CP-14 / "Dina steg" documents): after Resultat B and
  // "Avsluta för nu" the visible progression is "Första steget — klart" and
  // the paused status step. Booking is not a step until she herself chooses to
  // book from the saved feedback (decision = book ⇒ f01Paused is false again).
  if (s.f01Paused) {
    return [
      step("f01", "task", "done"),
      step("f01_paused", "feedback", "active"),
    ];
  }
  // An undecided stop: F-01 is done, nothing is active — "Boka vårdmöte"
  // only becomes a step when she chooses to book from the result (Ö-111
  // asks what Hem should offer her meanwhile).
  if (s.f01Stopped && !s.consultBooked && !s.consultHeld) {
    return [
      step("f01", "task", "done"),
      step("book_consult", "task", "upcoming"),
      step("intake", "task", "upcoming"),
      step("consult", "milestone", "upcoming"),
      step("labs", "task", "upcoming"),
      step("treatment_decision", "next_step", "upcoming"),
      step("treatment_start", "milestone", "upcoming"),
    ];
  }
  // TES-1094 — a decision counts only when one was PUBLISHED for a final
  // outcome.
  //
  // TES-1133 (Emelie 2026-10-06, the first prod patient) — and the published
  // decision is enough on its own. This used to require `treatmentActive`
  // too, but since TES-1071 a published positive decision activates nothing:
  // the treatment row stays `onboarding` until the doctor confirms the
  // prescription after payment. So "Behandlingsbeslut" stayed the active
  // rung, with its pre-decision body, for the whole time she was meant to be
  // activating her subscription, and she could not tell what came next.
  const decided = s.publishedDecision !== "none";
  // TES-1601 (TES-1600 S1) — with the meeting optional and none booked, the
  // declaration follows F-01 directly and the doctor's decision follows the
  // declaration, as on the transfer ladder. A meeting she books herself puts
  // both rungs back exactly as today.
  // TES-1656 (Emelie, TES-1653 §6) — and the two rungs are left out, not
  // shown as "Ej aktuellt": "Visa inte 'Boka vårdmöte' och 'Vårdmöte' med
  // 'Ej aktuellt'. De ska inte synas som moment patienten behöver förstå
  // eller ta sig förbi."
  const noMeeting =
    s.onboardingMeetingOptional === true && !s.consultBooked && !s.consultHeld;
  // Ordered gates: [key, type, done, attention?]
  const gates: Array<{
    key: JourneyStepKey;
    type: JourneyStepType;
    done: boolean;
    attention?: boolean;
    // TES-407 — the doctor decided this step isn't needed. Rendered as
    // "skipped" (ej aktuellt) and treated as passed for the positional
    // ladder, never removed from the list.
    skipped?: boolean;
    // TES-740 — beside the ladder: always active, never the first open rung.
    parallel?: boolean;
    // TES-1601 — not reached yet and not in the way: rendered upcoming, and
    // passed over when the ladder looks for its current rung.
    deferred?: boolean;
    // TES-1656 — not part of her path at all: left out of the list.
    omitted?: boolean;
  }> = [
    {
      key: "f01",
      type: "task",
      done: s.f01Done,
    },
    // v15 (Codex+supersedes the same-morning order):
    // booking comes BEFORE the health declaration — the declaration is filled
    // in ahead of the booked visit (matches the journeyState machine, where
    // meeting_booked prompts the declaration).
    {
      key: "book_consult",
      type: "task",
      done: s.consultBooked || s.consultHeld,
      omitted: noMeeting,
    },
    {
      // TES-675 / CP-13 — in the acute safety state a submitted F-02 reads as
      // DONE, not as "attention".
      //
      // The self-harm question was answered and the ladder still offered
      // "Börja hälsodeklarationen" for the form she had just sent, under the
      // attention line
      // "Baserat på det du har delat kan vi inte erbjuda dig den här behandlingen i nuläget".
      // Two things wrong at once: the app asked her to start something she
      // had finished, and it answered an acute safety signal with its
      // ordinary rejection copy — which her spec bans outright
      // ("Vyn får INTE innehålla: generell avslagsrubrik").
      //
      // `attention` means "something here needs YOUR action". For an acute
      // stop that is false: there is nothing for her to do in the form, the
      // acute path owns the situation, and the only honest thing the rung can
      // say is that the declaration is in. Marking it done needs no new copy
      // at all — the step falls back to its ordinary body and drops the CTA.
      key: "intake",
      type: "task",
      done: s.hasIntake && (s.intakeClean || s.acuteSafetyActive),
      attention: s.hasIntake && !s.intakeClean && !s.acuteSafetyActive,
    },
    {
      key: "consult",
      type: "milestone",
      done: s.consultHeld,
      omitted: noMeeting,
    },
    // TES-740 — the doctor decided with follow-up remaining (TES-736): the
    // labs rung would now read "done" on a sample never given, or block
    // "Behandlingsstart" if it read open. Neither is true. In that state the
    // rung is the parallel "Medicinsk uppföljning" track instead: her open
    // CP-06 tasks, beside the ladder, never ahead of the start.
    // TES-1094 — and only after a PUBLISHED positive decision: the rung's
    // copy says the doctor has approved the treatment.
    decided && s.publishedDecision === "approved" && s.medicalFollowUpOpen
      ? { key: "medical_follow_up", type: "task", done: false, parallel: true }
      : // TES-1094 (points 3 and 6) — her CP-06 tasks are done and no
        // decision has been published: "Medicinsk uppföljning" is the current
        // WAITING step. On the ladder, not beside it, and she waits rather
        // than acts (a next_step). /me sends its waiting copy.
        s.publishedDecision === "none" && s.medicalEvidenceWaiting
        ? { key: "medical_follow_up", type: "next_step", done: false }
        : // TES-1285 (CP-06 "Visnings- och återanvändningsregel") — before
          // the blood test is paid the sample cannot be given, so the rung
          // must not lead to "Hitta provtagningsställe". Every installed
          // build maps `labs` to FindLab and falls back to its bundled
          // "Hitta provtagning" button and sampling block whatever copy the
          // server sends, so the rung is `medical_follow_up` instead: a task
          // on the ladder (not parallel) that opens the checklist, where the
          // payment is. Its copy comes from the server (withLabsStepCopy).
          !decided && s.labsOrdered && s.labsAwaitingPayment === true
          ? { key: "medical_follow_up", type: "task", done: false }
          : {
              // TES-407 — "Blodprov vid behov" is ALWAYS in the list. Done once
              // labs were ordered and either reviewed or the decision closed the
              // chapter; skipped when the doctor decided WITHOUT ever ordering
              // labs — from the moment of that decision (TES-537 val 3), not
              // first at activation: between the two the patient was told labs
              // had been ordered.
              //
              // TES-1127 (Emelie 2026-10-06) — and skipped from the moment the
              // meeting is held, unless a blood test was ordered. Only the
              // doctor's explicit "Nytt blodprov" may make this rung active,
              // and that choice creates the case's LabOrder in the same
              // transaction (TES-954), so `labsOrdered` IS that choice. Before
              // this, a held meeting not yet documented left the rung as the
              // first open one, so it went active and told the first prod
              // patient "Läkaren har beställt blodprov" with the sampling
              // instructions while nothing had been ordered. The next active
              // rung is now "Behandlingsbeslut".
              key: "labs",
              type: "task",
              done: s.labsOrdered && (s.careFeedbackReceived || decided),
              skipped:
                !s.labsOrdered &&
                (decided || s.labsWaived === true || s.consultHeld),
              // TES-1601 — without a meeting, the submitted clean declaration
              // is where the held meeting was: the doctor reads it next. No
              // one has yet decided whether a blood test is needed, so the
              // rung is neither active ("Läkaren har beställt blodprov",
              // TES-1127's defect) nor "Ej aktuellt": it stays upcoming and
              // "Behandlingsbeslut" is the current, waiting step.
              deferred:
                noMeeting && s.hasIntake && s.intakeClean && !s.labsOrdered,
            },
    // The doctor decides — the patient waits. Never an "active task".
    // TES-1094 — done only on a published final decision (point 7).
    { key: "treatment_decision", type: "next_step", done: decided },
    // TES-1133 (Emelie 2026-10-06) — the subscription is a rung again, but
    // only after a PUBLISHED POSITIVE decision.
    // TES-1150 (Emelie 2026-10-07) — kept, with her copy, and the order is
    // hers: "Behandlingsbeslut ✓ → Aktivera abonnemanget → Hämta ut
    // läkemedlet, när uthämtning krävs → Behandlingsstart". Done only on the
    // verified subscription (`entitled`, the webhook's stored status). The QA baseline of 2026-08-19
    // had removed it ("CP-11 is the action, not a step"), and the first prod
    // patient then had no step telling her what came after the decision.
    // Active until the subscription is verified (trialing/active), done
    // after. A negative decision never shows it (TES-1069 point 13).
    //
    // Informational only (Simon, App Store): the app gets no link and no
    // button to the purchase page, only "Skicka länken igen", which re-sends
    // the doctor's e-mail. The server sends the copy, so app builds that
    // predate this (2.0.4) render it from their own `subscription` key.
    ...(s.publishedDecision === "approved"
      ? [
          {
            key: "subscription" as const,
            type: "task" as const,
            done: s.entitled,
          },
        ]
      : []),
    // TES-1150's "Hämta ut läkemedlet" follows the release, so it lives on
    // the released patient's start steps (TES-1159, startSteps below).
    // TES-407 — the ladder ends at the treatment actually starting, not at
    // the subscription. (Rarely seen as done here: once active+entitled the
    // phase routing flips to the under-treatment view.) After a paid
    // subscription it is the active rung while the doctor confirms the
    // prescription; /me sends the TES-1069 point 9 waiting copy for it.
    // TES-1133 — after a published negative decision it is "Ej aktuellt",
    // as the transfer ladder's last rung is after `cannot_transfer`.
    {
      key: "treatment_start",
      type: "milestone",
      done: s.treatmentActive && s.entitled,
      skipped: s.publishedDecision === "declined",
    },
  ];

  const shown = gates.filter((g) => !g.omitted);
  const firstOpen = shown.findIndex(
    (g) => !g.done && !g.skipped && !g.parallel && !g.deferred,
  );
  return shown.map((g, i) => {
    if (g.parallel) return parallelStep(g.key, g.type);
    if (g.skipped) return step(g.key, g.type, "skipped");
    if (g.attention) return step(g.key, g.type, "attention");
    if (g.done) return step(g.key, g.type, "done");
    return step(g.key, g.type, i === firstOpen ? "active" : "upcoming");
  });
}

// TES-450 — the prescription-transfer ladder (09 Surface Rules):
//   "1 Första steget · 2 Fördjupad hälsodeklaration & underlag · 3 Medicinsk granskning · 4 Eventuell komplettering · 5 Beslut om receptövertag · 6 Fortsatt behandling".
// Always six rows, always this order. A consultation is NEVER a row: a
// `meeting_required` review keeps step 3 active and the home card carries
// the booking. Step 4 is conditional in the sense of TES-407's "ej
// aktuellt" — rendered skipped when the doctor decided without one, never
// removed.
function transferSteps(s: JourneyStepsInput): JourneyStep[] {
  const r = s.transferReview;
  const status = r?.status ?? null;
  const decided = status === "approved" || status === "cannot_transfer";
  // TES-734 — a new blood test (labs_required) is her turn again, like a
  // komplettering: step 4 active, step 3 done for now.
  const completing =
    status === "completion_requested" || status === "labs_required";

  // 2 — F-02 + F-07. The review row opens only once both are in, so its
  // existence is the "done" signal. A hard-stopped F-02 is the same
  // attention case as the consultation ladder's intake rung.
  // TES-675 — same rule on the transfer ladder: an acute stop is not an
  // "attention" the patient can act on, and the rung must not re-ask for a
  // declaration she has already sent.
  const declaration: JourneyStepStatus = r
    ? "done"
    : s.hasIntake && s.acuteSafetyActive
      ? "done"
      : s.hasIntake && !s.intakeClean
        ? "attention"
        : "active";

  // 3 — the doctor's turn. Active while she is looking (incl. the fee-free
  // meeting she may ask for); done once she has either decided or sent the
  // patient a komplettering (the review reopens as pending_review after the
  // resubmit, and the rung goes active again).
  const review: JourneyStepStatus = !r
    ? "upcoming"
    : status === "pending_review" || status === "meeting_required"
      ? "active"
      : "done";

  // 4 — the patient's turn again, only if asked.
  const completion: JourneyStepStatus = completing
    ? "active"
    : r?.completionEverRequested
      ? "done"
      : decided
        ? "skipped"
        : "upcoming";

  // 5 — never active: the doctor decides, the patient waits. cannot_transfer
  // is the ladder's stop and reads as attention, same as a hard stop.
  const decision: JourneyStepStatus =
    status === "approved"
      ? "done"
      : status === "cannot_transfer"
        ? "attention"
        : "upcoming";

  // TES-1150 (Emelie 2026-10-07) — "Samma sekvens vid receptövertag":
  // "Behandlingsbeslut → Aktivera abonnemanget → eventuell uthämtning →
  // fortsatt behandling". After an approval the subscription rung sits
  // between the decision and the continued treatment, as on the
  // new-treatment ladder; the pickup follows the release, on the start steps
  // (TES-1159). 09 Surface Rules' six rows stay as they are before the
  // approval and after a refusal.
  const approved = status === "approved";
  const subscription: JourneyStepStatus = s.entitled ? "done" : "active";

  // 6 — active once approved and the subscription is verified (before
  // that, "Aktivera abonnemanget" is the active rung); done when the
  // treatment is live and entitled (rarely seen here: the phase routing
  // flips to the active loop then).
  const continued: JourneyStepStatus =
    s.treatmentActive && s.entitled
      ? "done"
      : approved
        ? s.entitled
          ? "active"
          : "upcoming"
        : status === "cannot_transfer"
          ? "skipped"
          : "upcoming";

  return [
    step("f01", "task", "done"),
    step("transfer_declaration", "task", declaration),
    step("transfer_review", "next_step", review),
    step("transfer_completion", "task", completion),
    step("transfer_decision", "next_step", decision),
    ...(approved ? [step("subscription", "task", subscription)] : []),
    step("transfer_continued", "milestone", continued),
  ];
}

// TES-1158 — the weekly check-in rung: done once this window's check-in is in,
// active only while the window counts for her, otherwise upcoming.
function checkinStatus(s: JourneyStepsInput): JourneyStepStatus {
  if (s.weeklyCheckinSubmitted) return "done";
  return s.weeklyCheckinCounts === false ? "upcoming" : "active";
}

// TES-740 — the follow-up outlives the activation (TES-736): the moment
// the subscription lands she is on this ladder, and her open CP-06 must
// not vanish with the pre-treatment one. Parallel, beside the loop.
// TES-1094 — the approved track only after a published positive decision;
// without one, her finished CP-06 is the waiting step instead.
function followUpSteps(s: JourneyStepsInput): JourneyStep[] {
  if (s.medicalFollowUpOpen && s.publishedDecision === "approved")
    return [parallelStep("medical_follow_up", "task")];
  if (s.medicalEvidenceWaiting && s.publishedDecision === "none")
    return [step("medical_follow_up", "next_step", "active")];
  return [];
}

// TES-1159 (Emelie 2026-10-07, point 6) — released, but the treatment has
// not started in Ella yet: "Hämta ut läkemedlet" while a new prescription
// waits to be collected (TES-1150), then "Behandlingsstart" with
// "Registrera din första dos" as the one remaining task. "Första veckan" is
// listed as what comes next and is never active here.
//
// Phase `active`, not `pre_treatment`, on purpose. Every app build hides the
// pre-treatment ladder once onboarding is complete (dinaStegSlot: phase
// pre_treatment + step active → no slot), so a released patient on that
// ladder saw no Dina steg at all, the pickup rung included.
function startSteps(s: JourneyStepsInput): JourneyStep[] {
  const pickup = pickupRung(s);
  const startKey: JourneyStepKey =
    s.patientGroup === "PRESCRIPTION_TRANSFER"
      ? "transfer_continued"
      : "treatment_start";
  return [
    ...(pickup ? [step("medication_pickup", "task", "active")] : []),
    step(startKey, "milestone", pickup ? "upcoming" : "active"),
    step("first_week", "milestone", "upcoming"),
    ...followUpSteps(s),
  ];
}

function atGoalSteps(s: JourneyStepsInput): JourneyStep[] {
  return [
    // NOT hardcoded "done". This catalog is reached on `goalReached ||
    // inMaintenance`, so a patient who never met her goal lands here the day
    // the derived phase flips to maintenance — twelve months of calendar time
    // after activation, nothing else. Asserting the milestone there told her
    // "Målvikt nådd ✓" while our OWN `goalReached` said false.
    //
    // This does not make the milestone verified — `goalReached` is still one
    // self-logged weight under a self-set number, which is not the confirmed
    // goal ZONE the flow spec asks for (see TES-473). It only stops the
    // surface claiming more than the data behind it.
    step("goal_reached", "milestone", s.goalReached ? "done" : "upcoming"),
    step("maintenance_plan", "next_step", s.inMaintenance ? "done" : "active"),
    step("stabilisation", "milestone", s.inMaintenance ? "active" : "upcoming"),
    // Recurring — marked so, never removed (TES-473, Ö-6).
    step("continued_followup", "task", checkinStatus(s), "weekly"),
    step("adjust_goal", "task", "upcoming"),
  ];
}

export function deriveJourneySteps(s: JourneyStepsInput): JourneySteps {
  return { ...deriveSteps(s), coachAccess: s.coachAccess };
}

function deriveSteps(
  s: JourneyStepsInput,
): Pick<JourneySteps, "phase" | "steps"> {
  // Macro-state routing: goal beats active beats onboarding — a patient who
  // reached her goal sees the at-goal view even while treatment continues.
  if (s.goalReached || s.inMaintenance) {
    return { phase: "at_goal", steps: atGoalSteps(s) };
  }
  // TES-1150 / TES-1159 — released: "Hämta ut läkemedlet" and
  // "Behandlingsstart" until her first dose is registered, then the loop.
  if (s.treatmentActive && s.entitled) {
    return {
      phase: "active",
      // TES-1634 (Emelie 2026-10-28) — "Dina steg används endast för den
      // linjära resan fram till behandlingsstart och tas bort helt när
      // behandlingen är aktiv." After the start steps the list is empty:
      // app 2.0.7 and later hide the section then, older builds are served
      // a phase that hides it (legacy-client.ts), and a build at
      // CLIENT_CONTRACT_CHANGES.renewalExplainer shows "Nästa
      // receptförnyelse" in its place (/me `prescriptionRenewal`).
      steps:
        pickupRung(s) || s.firstDoseOutstanding === true ? startSteps(s) : [],
    };
  }
  // TES-450 — the group decides which pre-treatment ladder she is on.
  if (s.patientGroup === "PRESCRIPTION_TRANSFER") {
    return { phase: "pre_treatment", steps: transferSteps(s) };
  }
  return { phase: "pre_treatment", steps: preTreatmentSteps(s) };
}

// TES-1023 (Emelie, Ö-277 a) — when "Dina steg" switches to the goal track
// ("Målvikt nådd ✓", next step "Underhållsplan"): only once the doctor has
// decided weight stabilisation. A logged weight at or below her goal is a
// reason to celebrate and to ask what she wants to focus on (TES-998), and
// her "keep the weight" choice is a wish for the medical assessment; neither
// changes her plan or the steps until the doctor has decided.
export function goalTrackReached(
  storedClinicalPhase: string | null | undefined,
): boolean {
  return (
    readStoredClinicalPhase(storedClinicalPhase) === "weight_stabilisation"
  );
}
